import React, { memo } from "react";
import { classNames } from "helpers";
import { ReactSVG } from "react-svg";
import { OfferSaleStatusHistory } from "services/api/fleet/vehicles/offers/types";
import { OfferSaleStatusId } from "services/api/fleet/vehicles/offers/consts";

interface OfferStatusToolbarProps {
  statusHistory: OfferSaleStatusHistory[];
  onToggle: (statusId: string, checked: boolean) => void;
  disabled?: boolean;
}

const STATUS_LABELS: Record<string, string> = {
  [OfferSaleStatusId.Draft]: "Predložak",
  [OfferSaleStatusId.Created]: "Kreirana",
  [OfferSaleStatusId.Sent]: "Poslana",
  [OfferSaleStatusId.Accepted]: "Prihvaćena",
  [OfferSaleStatusId.Paid]: "Plaćena",
};

const STATUS_ORDER = [OfferSaleStatusId.Draft, OfferSaleStatusId.Created, OfferSaleStatusId.Sent, OfferSaleStatusId.Accepted, OfferSaleStatusId.Paid];
const LOCKED_STATUSES: string[] = [OfferSaleStatusId.Draft, OfferSaleStatusId.Created];

const OfferStatusToolbar: React.FC<OfferStatusToolbarProps> = ({
  statusHistory,
  onToggle,
  disabled = false,
}) => {
  const orderedStatuses: OfferSaleStatusHistory[] = STATUS_ORDER.map((id) => {
    const existing = statusHistory.find((h) => h.offerSaleStatusId === id);
    return existing ?? {
      id: id,
      offerSaleStatusId: id,
      statusName: STATUS_LABELS[id] ?? id,
      statusColor: null,
      checked: false,
      checkedAt: null,
    };
  });

  // Find the last checked index to determine active status
  const lastCheckedIndex = orderedStatuses.reduce(
    (acc, s, i) => (s.checked ? i : acc),
    -1
  );

  return (
    <div className="flex border border-slate-200 rounded-sm overflow-hidden mb-4">
      {orderedStatuses.map((status, index) => {
        // Logika statusa:
        // - "Predložak" i "Kreirana" su zaključani (mijenjaju se samo kroz flow spremanja)
        // - "Poslana", "Prihvaćena" i "Plaćena" se mogu klikati tek kad je "Kreirana" označena
        // - Aktivni status = zadnji označeni, prošli statusi imaju kvačicu
        const isChecked = status.checked;
        const isCurrent = index === lastCheckedIndex + 1;
        const isDone = isChecked;
        const isLastDone = index === lastCheckedIndex;
        const isPast = isDone && !isLastDone;
        const isLocked = LOCKED_STATUSES.includes(status.offerSaleStatusId);
        const createdIndex = orderedStatuses.findIndex((s) => s.offerSaleStatusId === OfferSaleStatusId.Created);
        const isCreatedChecked = createdIndex >= 0 && orderedStatuses[createdIndex].checked;
        const isDisabled = disabled || isLocked || (!isCreatedChecked && !isLocked);

        return (
          <button
            key={status.offerSaleStatusId}
            type="button"
            disabled={isDisabled}
            onClick={() => onToggle(status.offerSaleStatusId, !status.checked)}
            className={classNames(
              "flex-1 flex items-center justify-center gap-2 py-2.5 px-3 text-sm transition-all duration-200 border-r last:border-r-0",
              isDone
                ? "bg-green-100 text-green-800 border-b-2 border-b-green-600 font-semibold"
                : isCurrent
                ? "bg-sky-50 text-sky-700 border-slate-200 font-medium"
                : "bg-white text-slate-400 border-slate-200 font-medium",
              !isDisabled && "cursor-pointer hover:opacity-80"
            )}
          >
            {isDone && (
              <ReactSVG
                src="/media/icons/check.svg"
                className="w-4 h-4 stroke-green-700"
              />
            )}
            <span>{status.statusName}</span>
          </button>
        );
      })}
    </div>
  );
};

export default memo(OfferStatusToolbar);
